import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Megaphone, Download, Copy, Check, ExternalLink, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin marketing-banner panel.
 * Lists all available banner variants × formats, lets admin preview & download
 * each as PNG, and copy the recommended click-through URL for ad placements.
 */
export const BannerPanel = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [copied, setCopied] = useState(null);
    // Authenticated image previews need a Bearer header, so we fetch as blob
    // once per item and keep an object URL.
    const [previews, setPreviews] = useState({});

    useEffect(() => {
        let active = true;
        api.get("/admin/banners")
            .then(async ({ data }) => {
                const list = data.items || [];
                if (!active) return;
                setItems(list);
                setLoading(false);
                const entries = await Promise.all(
                    list.map(async (it) => {
                        try {
                            const res = await api.get(
                                `/admin/banners/${it.variant}/${it.fmt}.png`,
                                { responseType: "blob" },
                            );
                            return [`${it.variant}-${it.fmt}`, URL.createObjectURL(res.data)];
                        } catch {
                            return null;
                        }
                    }),
                );
                if (!active) return;
                const map = {};
                entries.forEach((e) => { if (e) map[e[0]] = e[1]; });
                setPreviews(map);
            })
            .catch(() => {
                if (active) {
                    toast.error("Werbe-Banner konnten nicht geladen werden");
                    setLoading(false);
                }
            });
        return () => { active = false; };
    }, []);

    const download = async (item) => {
        const key = `${item.variant}-${item.fmt}`;
        setDownloadingKey(key);
        try {
            const res = await api.get(`/admin/banners/${item.variant}/${item.fmt}.png`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = item.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Banner heruntergeladen", { description: item.filename });
        } catch (err) {
            toast.error("Download fehlgeschlagen");
        } finally {
            setDownloadingKey(null);
        }
    };

    const buildClickUrl = (item) => {
        // Adds UTM parameters so the buyer can track ad clicks in PostHog.
        // Investor-banners (variant 4 + 5) deep-link to /investor;
        // the others land on the home page.
        const params = new URLSearchParams({
            utm_source: "banner",
            utm_medium: "display",
            utm_campaign: `motiv-${item.variant}`,
            utm_content: item.fmt,
        });
        const path = item.variant === 4 || item.variant === 5 ? "/investor" : "/";
        return `https://neubau-kaufen-wiesbaden.de${path}?${params.toString()}`;
    };

    const copyUrl = async (item) => {
        const key = `${item.variant}-${item.fmt}`;
        try {
            await navigator.clipboard.writeText(buildClickUrl(item));
            setCopied(key);
            toast.success("Klick-URL kopiert", { description: buildClickUrl(item) });
            setTimeout(() => setCopied(null), 2200);
        } catch {
            toast.error("Kopieren fehlgeschlagen");
        }
    };

    return (
        <div data-testid="admin-banner-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <span className="overline">Marketing</span>
                    <h2 className="font-heading text-2xl text-ink mt-1">
                        Werbebanner als PNG
                    </h2>
                    <p className="font-body text-sm text-stone-500 mt-1 max-w-2xl">
                        3 Motive in 2 Formaten (350×250 und 350×175). Direkt herunterladen
                        und in Ihrer Werbekampagne (Google Display, Newsletter, Portale)
                        einsetzen. Alle Banner sind so gebaut, dass sie auf die Landing Page
                        verlinkt werden – die UTM-getaggte Klick-URL gibt es per Klick aufs Kopieren-Symbol.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="p-12 flex justify-center">
                    <Loader2 className="animate-spin text-stone-400" size={24} />
                </div>
            ) : (
                <div className="space-y-10">
                    {[1, 2, 3, 4, 5].map((variant) => (
                        <BannerVariantBlock
                            key={variant}
                            variant={variant}
                            items={items.filter((i) => i.variant === variant)}
                            previews={previews}
                            onDownload={download}
                            onCopy={copyUrl}
                            downloadingKey={downloadingKey}
                            copied={copied}
                            buildClickUrl={buildClickUrl}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const BannerVariantBlock = ({
    variant,
    items,
    previews,
    onDownload,
    onCopy,
    downloadingKey,
    copied,
    buildClickUrl,
}) => {
    if (items.length === 0) return null;
    const first = items[0];
    const titleLabel = {
        1: 'Motiv 1 · Vorderhaus · „Hier klicken"-CTA',
        2: "Motiv 2 · Penthouse",
        3: "Motiv 3 · Loft-Maisonette",
        4: "Motiv 4 · Investor · 5 % AfA + KfW",
        5: "Motiv 5 · Investor · Wir vermieten für Sie",
    }[variant];

    return (
        <div className="bg-white border border-stone-200 p-6" data-testid={`banner-variant-${variant}`}>
            <div className="flex items-start gap-3 mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-brand-50 text-brand-600 shrink-0">
                    <Megaphone size={16} strokeWidth={1.6} />
                </div>
                <div>
                    <h3 className="font-heading text-lg text-ink leading-tight">
                        {titleLabel}
                    </h3>
                    <p className="font-body text-xs text-stone-500 mt-0.5">
                        {first.eyebrow} · {first.title}
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
                {items.map((item) => {
                    const key = `${item.variant}-${item.fmt}`;
                    const isDownloading = downloadingKey === key;
                    const wasCopied = copied === key;
                    return (
                        <div
                            key={key}
                            className="border border-stone-200"
                            data-testid={`banner-card-${item.variant}-${item.fmt}`}
                        >
                            <div className="bg-cream/60 p-4 flex items-center justify-center min-h-[180px]">
                                {previews[key] ? (
                                    <img
                                        src={previews[key]}
                                        alt={`Motiv ${variant} · ${item.dimensions}`}
                                        className="block shadow-md max-w-full"
                                        style={{ width: 350, height: "auto" }}
                                    />
                                ) : (
                                    <ImageIcon size={32} className="text-stone-300" />
                                )}
                            </div>
                            <div className="p-4 border-t border-stone-200">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="font-body text-sm font-semibold text-ink">
                                        {item.dimensions} px
                                    </div>
                                    <div className="font-body text-xs text-stone-500">
                                        {item.fmt === "medium" ? "Medium Rectangle" : "Querformat"}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => onDownload(item)}
                                        disabled={isDownloading}
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-white hover:bg-brand-500 transition-colors text-xs font-body font-medium tracking-wide disabled:opacity-60"
                                        data-testid={`banner-download-${item.variant}-${item.fmt}`}
                                    >
                                        {isDownloading ? (
                                            <Loader2 size={13} className="animate-spin" />
                                        ) : (
                                            <Download size={13} />
                                        )}
                                        PNG herunterladen
                                    </button>
                                    <button
                                        onClick={() => onCopy(item)}
                                        className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-body font-medium transition-colors ${
                                            wasCopied
                                                ? "border-brand-500 text-brand-600 bg-clay-50"
                                                : "border-stone-300 text-stone-600 hover:border-brand-500 hover:text-brand-600"
                                        }`}
                                        data-testid={`banner-copy-url-${item.variant}-${item.fmt}`}
                                        title={buildClickUrl(item)}
                                    >
                                        {wasCopied ? <Check size={13} /> : <Copy size={13} />}
                                        Klick-URL kopieren
                                    </button>
                                    <a
                                        href={buildClickUrl(item)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-2 border border-stone-200 text-stone-500 text-xs font-body hover:text-ink hover:border-stone-400 transition-colors"
                                        title="Vorschau öffnen"
                                        data-testid={`banner-preview-${item.variant}-${item.fmt}`}
                                    >
                                        <ExternalLink size={13} />
                                    </a>
                                </div>
                                <p className="font-body text-[11px] text-stone-400 mt-3 break-all">
                                    Ziel: <code>{buildClickUrl(item)}</code>
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
